import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Enable credentials for httpOnly cookie auth
axios.defaults.withCredentials = true;

const AdminLogin = () => {
  const [loginMode, setLoginMode] = useState('admin'); // 'admin' or 'sales'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      let response;
      if (loginMode === 'admin') {
        response = await axios.post(`${API}/admin/login`, { password });
      } else {
        response = await axios.post(`${API}/sales-rep/login`, { username, password });
      }
      
      // Store auth token in sessionStorage (for cross-origin Bearer auth)
      // httpOnly cookie also set by server (for same-origin fallback)
      sessionStorage.setItem('adminToken', response.data.token);
      sessionStorage.setItem('userRole', response.data.role);
      sessionStorage.setItem('userId', response.data.user_id);
      sessionStorage.setItem('userName', response.data.user_name);
      navigate('/admin/dashboard');
    } catch (err) {
      if (err.response && err.response.status === 401) {
        setError('Invalid credentials');
      } else if (err.response && err.response.status >= 500) {
        setError('Server is temporarily unavailable. Please try again later.');
      } else {
        setError('Cannot connect to server. Please check your connection.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white border border-slate-200 p-8">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_steel-build-pro/artifacts/imjdxz67_3d-metal-home-building-logo%202.jpg"
            alt="3D Metal Home Building"
            className="h-32 w-auto mx-auto mb-4"
          />
          <h1
            className="text-3xl font-bold text-slate-900 uppercase"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
            data-testid="admin-login-title"
          >
            CRM Login
          </h1>
          <p className="text-slate-600 mt-2">3D Metal Home Building</p>
        </div>

        {/* Login Mode Toggle */}
        <div className="flex mb-6 border rounded overflow-hidden">
          <button
            type="button"
            onClick={() => { setLoginMode('admin'); setError(''); }}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              loginMode === 'admin' 
                ? 'bg-orange-500 text-white' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Admin Login
          </button>
          <button
            type="button"
            onClick={() => { setLoginMode('sales'); setError(''); }}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              loginMode === 'sales' 
                ? 'bg-blue-500 text-white' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Sales Rep Login
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {loginMode === 'sales' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="w-full px-4 py-3 border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-slate-900"
                placeholder="Enter your username"
                data-testid="sales-username-input"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={`w-full px-4 py-3 border border-slate-300 focus:ring-1 text-slate-900 ${
                loginMode === 'admin' 
                  ? 'focus:border-orange-500 focus:ring-orange-500' 
                  : 'focus:border-blue-500 focus:ring-blue-500'
              }`}
              placeholder={loginMode === 'admin' ? 'Enter admin password' : 'Enter your password'}
              data-testid="admin-password-input"
            />
          </div>

          {error && (
            <p className="text-red-500 text-sm" data-testid="admin-login-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className={`w-full font-bold uppercase tracking-wider py-3 transition-colors disabled:opacity-50 text-white ${
              loginMode === 'admin' 
                ? 'bg-orange-500 hover:bg-orange-600' 
                : 'bg-blue-500 hover:bg-blue-600'
            }`}
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
            data-testid="admin-login-submit"
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <a
            href="/"
            className="text-sm text-slate-600 hover:text-orange-500 transition-colors"
            data-testid="back-to-site-link"
          >
            ← Back to website
          </a>
        </div>

        <div className="mt-8 p-4 bg-slate-100 rounded text-xs text-slate-600">
          <p className="font-semibold mb-1">CRM Access:</p>
          <p>Admin: Use admin password</p>
          <p>Sales Rep: Use your username and password</p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
